import { getDomainThemeDefaults, normalizeTemplateDomain } from './TemplateTheme.jsx';

const DOMAIN_PRIMARY_PALETTES = {
  EDUCATION: ['#3B9394', '#23196E', '#2563eb', '#008060', '#746EAC'],
  WAAS: ['#07111f', '#0b0f16', '#c9a46a', '#d8c2b6'],
  RETAIL: ['#0891b2', '#B88E2F', '#232f3e'],
  FLEX_OFFICE: ['#2563eb', '#1d4ed8', '#0f172a']
};

const DOMAIN_SECONDARY_PALETTES = {
  EDUCATION: ['#746EAC', '#87927c', '#746eac'],
  WAAS: ['#d8c2b6', '#5f6670'],
  RETAIL: ['#232f3e', '#6b7280'],
  FLEX_OFFICE: ['#0f172a', '#64748b']
};

const normalizeColor = (value) => String(value || '').trim().toLowerCase();

export const isLinkedThemeColor = (sectionValue, themeValue, linkedDefaults = []) => {
  if (!sectionValue) return true;
  const normalizedSection = normalizeColor(sectionValue);
  const normalizedTheme = normalizeColor(themeValue);
  if (normalizedTheme && normalizedSection === normalizedTheme) return true;
  return linkedDefaults.some((item) => normalizeColor(item) === normalizedSection);
};

export const resolveThemeLinkedColor = (sectionValue, themeValue, linkedDefaults = [], fallback = '') => {
  if (isLinkedThemeColor(sectionValue, themeValue, linkedDefaults)) {
    return themeValue || fallback;
  }
  return sectionValue || themeValue || fallback;
};

export const getDomainLinkedPalettes = (domainType) => {
  const domain = normalizeTemplateDomain(domainType);
  return {
    primary: DOMAIN_PRIMARY_PALETTES[domain] || DOMAIN_PRIMARY_PALETTES.EDUCATION,
    secondary: DOMAIN_SECONDARY_PALETTES[domain] || DOMAIN_SECONDARY_PALETTES.EDUCATION
  };
};

export const resolveSectionStyleForReader = (section = {}, theme = {}, domainType = 'EDUCATION') => {
  const style = { ...(section.style || {}) };
  const overrides = style.themeOverrides || {};
  const palettes = getDomainLinkedPalettes(domainType);
  const defaults = getDomainThemeDefaults(domainType);

  const resolvedTheme = {
    primary: theme.primary || defaults.primary,
    secondary: theme.secondary || defaults.secondary,
    background: theme.background || defaults.background,
    color: theme.color || defaults.color
  };

  if (!overrides.accentColor) style.accentColor = resolveThemeLinkedColor(style.accentColor, resolvedTheme.primary, palettes.primary, defaults.primary);
  if (!overrides.secondaryColor) style.secondaryColor = resolveThemeLinkedColor(style.secondaryColor, resolvedTheme.secondary, palettes.secondary, defaults.secondary);
  if (!overrides.backgroundColor) style.backgroundColor = resolveThemeLinkedColor(style.backgroundColor, resolvedTheme.background, [defaults.background, '#ffffff', '#f8fafc', '#f4f5f6', '#e6e8eb'], defaults.background);
  if (!overrides.color) style.color = resolveThemeLinkedColor(style.color, resolvedTheme.color, [defaults.color, '#111827', '#1f2937', '#1e293b', '#0b0f16', '#3e342e'], defaults.color);

  return { ...section, style };
};

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Inject `section.layout.elementLayout` from `attributes.elementLayouts[pageKey][sectionId]`
 * when the section does not already carry its own layout data. This mirrors the
 * `sectionWithDurableLayout` mapping that exists in the canvas builder's preview
 * (`CanvasRuntimePage.jsx`) so text/style/geometry edits authored in the builder
 * are applied on the published user-facing page via `SectionLayoutBoundary`.
 */
const withDurableElementLayout = (section, elementLayoutsForPage) => {
  if (!isPlainObject(section)) return section;
  if (!isPlainObject(elementLayoutsForPage)) return section;
  const sectionId = section.sectionId || section.id;
  if (!sectionId) return section;
  const mirroredLayout = elementLayoutsForPage[sectionId];
  if (!isPlainObject(mirroredLayout)) return section;
  const layout = isPlainObject(section.layout) ? section.layout : {};
  if (isPlainObject(layout.elementLayout)) return section; // already set — respect it
  return {
    ...section,
    layout: {
      ...layout,
      elementLayout: mirroredLayout,
    },
  };
};

export const resolveTemplateSectionsForReader = (template, domainType) => {
  const theme = template?.attributes?.theme || {};
  const resolvedDomain = normalizeTemplateDomain(domainType || template?.domain?.type);
  if (!Array.isArray(template?.pages)) return template;

  const elementLayouts = isPlainObject(template?.attributes?.elementLayouts)
    ? template.attributes.elementLayouts
    : null;

  return {
    ...template,
    pages: template.pages.map((page) => {
      const pageKey = page.pageKey || page.key;
      const layoutsForPage = elementLayouts && pageKey && isPlainObject(elementLayouts[pageKey])
        ? elementLayouts[pageKey]
        : null;
      return {
        ...page,
        sections: (page.sections || []).map((section) => {
          const withLayout = layoutsForPage
            ? withDurableElementLayout(section, layoutsForPage)
            : section;
          return resolveSectionStyleForReader(withLayout, theme, resolvedDomain);
        }),
      };
    }),
  };
};
